import { assertEvent } from 'xstate';

import { type DeliveryOrderDetailInput } from '@sg/graphql-schema';

import {
  type AddUpsellToBagParams,
  type FetchUpsellsParams,
  type LockGroupOrderParams,
} from './ordering-machine.actors';
import { type OrderingContext } from './ordering-machine.context';
import { type OrderingEvent } from './ordering-machine.events';

// ─────────────────────────────────────────────────────────────────────────────

export type OrderingMachineInput<InputType extends Record<string, unknown> | void = void> = (args: {
  context: OrderingContext;
  event: OrderingEvent;
}) => InputType;

// ─────────────────────────────────────────────────────────────────────────────

export const getAddUpsellToBagInput: OrderingMachineInput<AddUpsellToBagParams> = ({
  context,
  event,
}) => {
  assertEvent(event, 'ADD_UPSELL_TO_BAG');

  const { deliveryOrderDetail } = context.cart;
  const deliveryDetails: DeliveryOrderDetailInput | undefined = deliveryOrderDetail
    ? { ...deliveryOrderDetail, addressId: deliveryOrderDetail.address?.id ?? '' }
    : undefined;

  return {
    productId: event.params.productId,
    productName: event.params.productName,
    quantity: event.params.quantity,
    isPersonalized: event.params.isPersonalized,
    railPosition: event.params.railPosition,
    railItemCount: context.upsellRailSnapshot?.railItemCount ?? 0,
    personalizedItemCount: context.upsellRailSnapshot?.personalizedItemCount ?? 0,
    deliveryDetails,
    cart: context.cart,
  };
};

export const getFetchUpsellsInput: OrderingMachineInput<FetchUpsellsParams> = ({ context }) => ({
  restaurantId: context.cart.restaurantId,
  orderChannel: context.cart.orderChannel,
});

export const getLockGroupOrderInput: OrderingMachineInput<LockGroupOrderParams> = ({ context }) => {
  const { groupOrderLockReason } = context;

  if (!groupOrderLockReason) throw new Error('Group order lock reason is required');

  return { reason: groupOrderLockReason };
};
